// packages
import { h } from 'preact';
import * as d3 from 'd3';
import * as moment from 'moment';
import _ from 'lodash';
import { wordwrap } from 'd3-jetpack';
import { customDateFormat } from '../utils/chart-utils';

// data
import data from '../../../data/data.json';

// components
import PureComponent from './PureComponent';

class MiniEmploymentByIndustry extends PureComponent {
  constructor() {
    super();

    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.createChart = this.createChart.bind(this);
    this.redrawChart = this.redrawChart.bind(this);

    this.state = {
      containerWidth: null,
      bodyWidth: null,
      breakPoint: 544,
    };
  }

  createChart(industry, index) {
    let { containerWidth, bodyWidth, breakPoint } = this.state;
    let height;

    if (containerWidth > breakPoint) height = 200;
    else height = 225;

    let margin = {
      top: 10,
      right: 5,
      left: 40,
      bottom: 50,
    };

    // Establishes as isMobile function
    const isMobile = bodyWidth < breakPoint;

    let chartWidth = isMobile ? containerWidth : containerWidth / 3 - 30,
      chartHeight = height - margin.top - margin.bottom;

    // grab the data we need
    let rateData = data.employment_change_industry.filter(
      d => d.industry == industry
    );

    // changing to moment date+time objects
    rateData.forEach(d => (d.date = moment(d.date, 'MMM-YY')));

    // filter for after a date
    rateData = rateData.filter(d =>
      d.date.isSameOrAfter(moment('Sep-19', 'MMM-YY'))
    );

    // grabbing totals and ranges
    let dateRange = d3.extent(rateData, d => d.date),
      dataMax = d3.max(
        data.employment_change_industry,
        d => d.monthly_change_thousands
      ),
      dataMin = d3.min(rateData, d => d.monthly_change_thousands),
      dataExtentPercent = d3.extent(
        data.employment_change_industry,
        d => d.monthly_change_percent
      );

    let domain;
    if (dataMin < -150000) domain = [-600000, 200000];
    if (dataMin > -150000) domain = [-200000, 200000];

    const dates = {
      pandemicRecessionBegin: moment('2020-03-01'),
      pandemicRecessionEnd: dateRange[1],
    };

    // append svg
    let svg = d3
        .select(
          `.mini-industry-${industry
            .replace(/[ ]*,[ ]*|[ ]+/g, ' ')
            .split(' ')
            .join('-')}`
        )
        .append('svg')
        .attr('width', chartWidth)
        .attr('height', height),
      g = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

    //  set scale domains
    let x = d3
      .scaleBand()
      .domain(rateData.map(d => d.date))
      .range([0, chartWidth - margin.right - margin.left]);
    let y = d3
      .scaleLinear()
      .domain([-0.2, 0.2])
      .range([chartHeight, 0])
      .nice();

    let xAxis = g.append('g').attr('class', 'axis axis--x industry-axis'),
      yAxis = g.append('g').attr('class', 'axis axis--y industry-axis');

    xAxis
      .call(
        d3
          .axisBottom(x)
          .tickValues(
            x.domain().filter(function(d) {
              if (d.format('M') == 1) return d;
            })
          )
          .tickFormat(customDateFormat)
      )
      .attr('transform', `translate(0, ${chartHeight})`);

    yAxis.call(
      d3
        .axisLeft(y)
        .ticks(3)
        .tickSize(-(chartWidth - margin.right - margin.left))
        .tickPadding(10)
        .tickFormat(d => {
          if (d == 0) return '0%';
          else
            return `${d > 0 ? '+' : ''}${d3
              .format('.0%')(d)
              .replace(/G/, 'B')}`;
        })
    );

    drawEventArea(
      dates.pandemicRecessionBegin,
      dates.pandemicRecessionEnd,
      'Pandemic',
      dates.pandemicRecessionEnd.clone(),
      'end',
      index
    );

    d3.selectAll('.mini-industry .area-text text tspan')
      .nodes()
      .forEach(function(d) {
        if (d.textContent == 'Pandemic ') d.textContent = 'Pandemic';
      });

    let stackedData = d3.stack().keys(['monthly_change_percent'])(rateData);

    let barGroups = g
      .append('g')
      .selectAll('g')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('class', 'bar-groups');

    let barRects = barGroups
      .selectAll('rect')
      .data(function(d) {
        return d;
      })
      .enter()
      .append('rect');

    barRects
      .attr('fill', d => {
        let diff = y(d[0]) - y(d[1]);
        if (diff >= 0) return '#529aad';
        if (diff < 0) return '#d13926';
      })
      .attr('x', function(d) {
        return x(d.data.date);
      })
      .attr('y', function(d) {
        let diff = y(d[0]) - y(d[1]);
        if (diff >= 0) return y(d[1]);
        if (diff < 0) return y(d[0]);
      })
      .attr('height', function(d) {
        let diff = y(d[0]) - y(d[1]);
        return Math.abs(diff);
      })
      .attr('width', x.bandwidth());

    function drawEventArea(
      beginDate,
      endDate,
      labelText,
      labelTextDate,
      labelAnchor,
      index
    ) {
      let labels = g.append('g').attr('class', 'area-label-elements');

      let elements = labels
        .selectAll('g')
        .data(getLabeledData(rateData, ['']))
        .enter()
        .append('g')
        .attr('class', 'area-label-element');

      elements
        .append('rect')
        .attr('class', 'column')
        .attr('fill', '#000')
        .attr('opacity', 0.09)
        .attr('x', x(moment(beginDate, 'YYYY MMM')))
        .attr('y', y(y.domain()[1]))
        .attr(
          'width',
          x(moment(endDate, 'YYYY MMM')) -
            x(moment(beginDate, 'YYYY MMM')) +
            x.bandwidth()
        )
        .attr('height', y(y.domain()[0]) - y(y.domain()[1]));

      if (index == 0) {
        let text = elements
          .append('g')
          .attr('class', 'area-text')
          .attr('text-anchor', labelAnchor);

        text
          .append('text')
          .text('')
          .tspans(wordwrap(labelText, 10));

        text.attr(
          'transform',
          'translate(' +
            (containerWidth > breakPoint
              ? x(labelTextDate)
              : x(labelTextDate)) +
            ',' +
            y(y.domain()[0] + 0.03) +
            ')'
        );
      }
    }

    function getLabeledData(data, dates) {
      // always annotate the latest datapoint
      let labeledData = [data[data.length - 1]];
      dates.forEach(date => {
        date = moment(date, 'YYYY MMM'); // format needs to match date format in the data
        let foundData = data.find(d => d.date.isSame(date));
        if (foundData) {
          labeledData.push(foundData);
        }
      });
      return labeledData;
    }
  }

  redrawChart() {
    const { industry, index } = this.props;

    d3.selectAll(
      `.mini-industry-${industry
        .replace(/[ ]*,[ ]*|[ ]+/g, ' ')
        .split(' ')
        .join('-')} svg`
    ).remove();

    let { containerWidth, bodyWidth } = this.getContainerWidth();
    this.setState({ containerWidth: containerWidth, bodyWidth: bodyWidth });

    this.createChart(industry, index);
  }

  getContainerWidth() {
    let base = d3.select('.mini-industry-grid');
    const body = d3.select('body');

    const containerWidth = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { containerWidth, bodyWidth };
  }

  componentDidMount() {
    const { industry, index } = this.props;
    let { containerWidth, bodyWidth } = this.getContainerWidth();

    this.setState(
      { containerWidth: containerWidth, bodyWidth: bodyWidth },
      () => {
        this.createChart(industry, index);
      }
    );

    window.addEventListener(
      'resize',
      _.debounce(() => {
        this.redrawChart();
      }, 200)
    );
  }

  componentWillUnmount() {
    window.addEventListener(
      'resize',
      _.debounce(() => {
        this.redrawChart();
      }, 200)
    );
  }

  render() {
    const { industry } = this.props;

    return (
      <div
        class={`mini-industry mini-industry-${industry
          .replace(/[ ]*,[ ]*|[ ]+/g, ' ')
          .split(' ')
          .join('-')}`}
      ></div>
    );
  }
}

export default MiniEmploymentByIndustry;
